import { Injectable } from '@angular/core';
import { assert } from '../../../../../../../core/assert/assert';
import {
  TimeBlockDayOrWeekFixIntervalType,
  TimeBlockDayOrWeekVariableIntervalType,
} from '../../../../../../../core/models/timeblock/time-block-view-type.model';
import { ShiveRenderService } from '../../../../../../../core/services/rendering/shive-render.service';
import {
  CSSPressedClass,
  CSSSmallTimeBlockClass,
} from '../../../../../util/calendar-style.constants';
import { TimeBlockElementSelectorService } from '../../../rendering/time-block-element-selector.service';
import { smallTimeBlockHeight } from '../../../time-block.constants';
import { ITimeBlockComponentItem } from '../../../types/time-block-component-item-types';
import {
  CollisionHandlerService,
  IntersectionType,
} from '../collision-handling/collision-handler.service';
import { DragPart } from './drag-part';
import { TimeBlockDragResizeControllerService } from './time-block-drag-resize-handling/time-block-drag-resize-controller.service';

@Injectable()
export class DragHandlerService {
  constructor(
    private readonly shiveRenderService: ShiveRenderService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
  ) {}

  public configureDragHandlers(timeBlock: ITimeBlockComponentItem): void {
    if (
      timeBlock.timeBlockModel.timeBlockViewType instanceof TimeBlockDayOrWeekVariableIntervalType
    ) {
      this.configureVerticalDragHandlers(timeBlock);
    }
  }

  public configureVerticalDragHandlers(timeBlock: ITimeBlockComponentItem): void {
    const geometryData = timeBlock.timeBlockModel.timeBlockViewType!.geometryData!;
    if (
      timeBlock.timeBlockModel.timeBlockViewType instanceof TimeBlockDayOrWeekFixIntervalType &&
      geometryData.height <= 0
    ) {
      throw new Error('Time block model height is invalid.');
    }

    this.toggleSmallTimeBlockCSSClass(timeBlock);
  }

  /**
   * Flip top and bottom geometry data. The time attributes will be updated in the updateTimeBlockSchedule() function later.
   */
  public flipVerticalDragEdgeSelf(minimumTimeBlockHeight: number): void {
    const geometryData =
      this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel
        .timeBlockViewType!.geometryData!;
    const calTopToTimeBlockBottom = geometryData.calendarTopEdgeToTimeBlockBottomEdge;
    const calBottomToTimeBlockTop = geometryData.calendarBottomEdgeToTimeBlockTopEdge;
    CollisionHandlerService.collisionType = IntersectionType.IntersectNone;

    if (geometryData.top === 0 || geometryData.bottom === 0) {
      this.timeBlockDragResizeControllerService.DragPart =
        geometryData.top === 0 ? DragPart.Bottom : DragPart.Top;
      return;
    }

    let dragPart = this.timeBlockDragResizeControllerService.DragPart;
    if (dragPart === DragPart.Top) {
      geometryData.top = calTopToTimeBlockBottom > 0 ? calTopToTimeBlockBottom : 0;
      geometryData.calendarBottomEdgeToTimeBlockTopEdge = geometryData.bottom;
      geometryData.bottom -= minimumTimeBlockHeight;
      geometryData.calendarTopEdgeToTimeBlockBottomEdge = geometryData.top + minimumTimeBlockHeight;
      dragPart = DragPart.Bottom;
    } else {
      geometryData.bottom = calBottomToTimeBlockTop > 0 ? calBottomToTimeBlockTop : 0;
      geometryData.calendarTopEdgeToTimeBlockBottomEdge = geometryData.top;
      geometryData.top -= minimumTimeBlockHeight;
      geometryData.calendarBottomEdgeToTimeBlockTopEdge =
        geometryData.bottom + minimumTimeBlockHeight;
      dragPart = DragPart.Top;
    }

    if (geometryData.top < 0 || geometryData.bottom < 0) {
      throw new Error('Invalid top or bottom value.');
    }

    geometryData.height = minimumTimeBlockHeight;
    this.timeBlockDragResizeControllerService.DragPart = dragPart;
    this.setVerticalDragEdgeSwitchStyling();
  }

  /**
   * Add the correct CSS classes when switching the drag edges.
   */
  public setVerticalDragEdgeSwitchStyling(): void {
    const dragPart = this.timeBlockDragResizeControllerService.DragPart;

    const firstDragHandle = TimeBlockElementSelectorService.getTimeBlockFirstHandle(
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );
    const secondDragHandle = TimeBlockElementSelectorService.getTimeBlockSecondHandle(
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );

    this.shiveRenderService.removeClass(firstDragHandle, CSSPressedClass);
    this.shiveRenderService.removeClass(secondDragHandle, CSSPressedClass);

    if (dragPart === DragPart.Top) {
      this.shiveRenderService.addClass(firstDragHandle, CSSPressedClass);
    } else {
      this.shiveRenderService.addClass(secondDragHandle, CSSPressedClass);
    }
  }

  public handleHorizontalDragEdgeSwitchStyle(dragPart: DragPart): void {
    const firstDragHandle = TimeBlockElementSelectorService.getTimeBlockFirstHandle(
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );
    const secondDragHandle = TimeBlockElementSelectorService.getTimeBlockSecondHandle(
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );

    // If for example the left drag edge handler is pressed, but the right handler is hovered, switch drag part
    // so that the old drag bar icon vanishes immediately.
    if (firstDragHandle!.matches(':hover')) {
      dragPart = DragPart.Left;
      this.timeBlockDragResizeControllerService.DragPart = DragPart.Left;
    }

    if (secondDragHandle!.matches(':hover')) {
      dragPart = DragPart.Right;
      this.timeBlockDragResizeControllerService.DragPart = DragPart.Right;
    }

    if (dragPart === DragPart.Right) {
      this.shiveRenderService.removeClass(firstDragHandle, CSSPressedClass);
      this.shiveRenderService.addClass(secondDragHandle, CSSPressedClass);
    } else if (dragPart === DragPart.Left) {
      this.shiveRenderService.removeClass(secondDragHandle, CSSPressedClass);
      this.shiveRenderService.addClass(firstDragHandle, CSSPressedClass);
    }
  }

  public toggleSmallTimeBlockCSSClass(timeBlock: ITimeBlockComponentItem): void {
    assert(timeBlock.timeBlockModel.componentRef !== null, 'No time block component ref found.');

    const geometryData = timeBlock.timeBlockModel.timeBlockViewType!.geometryData!;

    if (geometryData.height <= smallTimeBlockHeight) {
      this.shiveRenderService.addClass(
        timeBlock.timeBlockModel.componentRef!.instance.timeBlockHTMLWrapper.nativeElement,
        CSSSmallTimeBlockClass,
      );
    } else {
      this.shiveRenderService.removeClass(
        timeBlock.timeBlockModel.componentRef!.instance.timeBlockHTMLWrapper.nativeElement,
        CSSSmallTimeBlockClass,
      );
    }
  }
}
